import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import ToggleOnRoundedIcon from "@mui/icons-material/ToggleOnRounded";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

// Data
import usersTableData from "layouts/users/data/usersTableData";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AlertBox from "components/Alert/alert";
import { allUsers } from "services/apiService";
import { deleteUser } from "services/apiService";
import { setUploadAccess } from "services/apiService";
import Loader from "components/Loader/loader";
import PermissionsDialog from "components/Permission/permission";
import { getAllIndexes } from "services/apiService";
import { toggleUserStatus } from "services/apiService";
import { toggleVerifiedBadge } from "services/apiService";
import { toggleTrustedSeller } from "services/apiService";

const ROLE_OPTIONS = [
  { value: "", label: "All Roles" },
  { value: "buyer", label: "Buyer" },
  { value: "seller", label: "Seller" },
];

const BOOL_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

function UserManagement() {
  const [searchParams] = useSearchParams();
  const [people, setPeople] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(10);
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState({});

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [role, setRole] = useState(searchParams.get("role") || "");
  const [isVerifiedDealer, setIsVerifiedDealer] = useState(searchParams.get("isVerifiedDealer") || "");
  const [verifiedBadge, setVerifiedBadge] = useState("");
  const [isActive, setIsActive] = useState("");

  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    isDelete: false,
    userId: "",
  });
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    getAllUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageCount, page, role, isVerifiedDealer, verifiedBadge, isActive, debouncedSearch]);

  const updateFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const hasActiveFilters = Boolean(search || role || isVerifiedDealer || verifiedBadge || isActive);

  const clearFilters = () => {
    setSearch("");
    setRole("");
    setIsVerifiedDealer("");
    setVerifiedBadge("");
    setIsActive("");
    setPage(1);
  };

  useEffect(() => {
    getIndex();
  }, []);

  const getIndex = async () => {
    try {
      const response = await getAllIndexes();
      if (response?.success) {
        setPermissions(response?.data)
      }
    } catch (err) {
      console.error("Fetch indexes failed:", err);
    }
  };

  const getAllUsers = async () => {
    setLoading(true);
    try {
      const response = await allUsers({
        search: debouncedSearch || undefined,
        role: role || undefined,
        isVerifiedDealer: isVerifiedDealer || undefined,
        verifiedBadge: verifiedBadge || undefined,
        isActive: isActive || undefined,
        page,
        limit: pageCount,
      });
      if (response?.success) {
        let users = response?.data?.users || [];
        let total = response?.data?.total ?? users.length;

        if (verifiedBadge !== "") {
          const wantVerified = verifiedBadge === "true";
          users = users.filter((u) => !!u?.verifiedBadge === wantVerified);
          total = users.length;
        }

        setPeople(users);
        setTotalUsers(total);
      }
    } catch (err) {
      console.error("Fetch users failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = async (data) => {
    setLoading(true);
    const payload = {
      userId: data?.id,
    };

    try {
      const response = await setUploadAccess(payload);
      if (response?.success) {
        setSelectedUserId(null);
        setSelectedPermissions({});
        setOpen(false);

        getAllUsers();

        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: response?.message?.message,
          isDelete: false,
          userId: response?.message?.name || "Twister User",
        });
      } else {
        setAlert({
          open: true,
          type: "error",
          title: "Oops!",
          message: response?.message?.message || "Something went wrong",
          isDelete: false,
          userId: response?.message?.name || "Twister User",
        });
      }
    } catch (error) {
      console.error("Error updating upload access:", error);
    } finally {
      setLoading(false);
    }
  };

  const onConfirm = async () => {
    const { isDelete, userId } = alert;
    const actionType = isDelete ? "deleted" : "blocked";

    try {
      const res = isDelete ? await deleteUser({ userId }) : await toggleUserStatus(userId);

      const alertType = res?.error ? "error" : "success";
      const alertTitle = res?.error ? "Oops!" : "Success";
      const alertMessage = res?.message || `User successfully ${actionType}.`;

      setAlert({
        open: true,
        type: alertType,
        title: alertTitle,
        message: alertMessage,
        isDelete: false,
        userId: "",
      });

      if (!res?.error) {
        getAllUsers();
      }
    } catch (err) {
      console.error("Action failed:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: `Failed to ${isDelete ? "delete" : "block"} user.`,
        isDelete: false,
        userId: "",
      });
    }
  };

  const handleDelete = async (userId) => {
    setAlert({
      open: true,
      type: "confirm",
      title: "Confirm Delete",
      message: "Are you sure you want to delete this user?",
      isDelete: true,
      userId: userId,
    });
  };

  const handleToggleBlock = async (userId, currentStatus) => {
    if (currentStatus) {
      setAlert({
        open: true,
        type: "confirm",
        title: "Confirm Block",
        message: "Are you sure you want to block this user?",
        isDelete: false,
        userId: userId,
      });
    } else {
      try {
        const res = await toggleUserStatus(userId);
        if (res?.error) {
          setAlert({
            open: true,
            type: "error",
            title: "Opps!",
            message: res?.message,
            isDelete: false,
            userId: userId,
          });
        } else {
          setAlert({
            open: true,
            type: "success",
            title: "Success",
            message: res?.message,
            isDelete: false,
            userId: "",
          });
          getAllUsers();
        }
      } catch (err) {
        console.error("Update status failed:", err);
      }
    }
  };

  const handleToggleVerifiedBadge = async (userId) => {
    try {
      const res = await toggleVerifiedBadge(userId);
      if (res?.error) {
        setAlert({
          open: true,
          type: "error",
          title: "Opps!",
          message: res?.message,
          isDelete: false,
          userId: "",
        });
      } else {
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: res?.message || "Verified badge updated successfully.",
          isDelete: false,
          userId: "",
        });
        getAllUsers();
      }
    } catch (err) {
      console.error("Update verified badge failed:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to update verified badge.",
        isDelete: false,
        userId: "",
      });
    }
  };

  const handleToggleTrustedSeller = async (userId) => {
    try {
      const res = await toggleTrustedSeller(userId);
      if (res?.error) {
        setAlert({
          open: true,
          type: "error",
          title: "Opps!",
          message: res?.message,
          isDelete: false,
          userId: "",
        });
      } else {
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: res?.message || "Trusted seller status updated successfully.",
          isDelete: false,
          userId: "",
        });
        getAllUsers();
      }
    } catch (err) {
      console.error("Update trusted seller failed:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to update trusted seller status.",
        isDelete: false,
        userId: "",
      });
    }
  };

  const handleAllowPermission = (userId, hasUploadAccess) => {
    setSelectedUserId(userId);
    setSelectedPermissions(hasUploadAccess);
    // setOpen(true);
    handleSelectUser(userId)
  }

  const totalPages = Math.max(1, Math.ceil(totalUsers / pageCount));

  const { columns, rows } = useMemo(() => {
    return usersTableData({
      peopleList: people,
      onDelete: handleDelete,
      onToggleBlock: handleToggleBlock,
      onAllowPermission: handleAllowPermission,
      onToggleVerifiedBadge: handleToggleVerifiedBadge,
      onToggleTrustedSeller: handleToggleTrustedSeller,
    });
  }, [people]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && <Loader />}
      <AlertBox
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={() => {
          onConfirm();
          setAlert({ ...alert, open: false });
        }}
      />

      <PermissionsDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setSelectedUserId(null);
          setSelectedPermissions({});
        }}
        permissionsList={permissions}
        selectedPermissions={selectedPermissions}
        onSubmit={(data) => {
          const hasChanges = Object.keys(data).length > 0;

          if (!hasChanges) {
            setOpen(false);
            return;
          }
          handleSelectUser(data);
        }}
      />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Users
                </MDTypography>
              </MDBox>

              <MDBox px={3} pt={3}>
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                >
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <FilterListRoundedIcon fontSize="small" color="secondary" />
                    <MDTypography variant="button" fontWeight="bold" color="text">
                      Filters
                    </MDTypography>
                  </MDBox>
                  {hasActiveFilters && (
                    <MDButton
                      variant="text"
                      color="secondary"
                      size="small"
                      startIcon={<ClearRoundedIcon fontSize="small" />}
                      onClick={clearFilters}
                    >
                      Clear filters
                    </MDButton>
                  )}
                </MDBox>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      select
                      label="Role"
                      fullWidth
                      size="medium"
                      value={role}
                      onChange={(e) => updateFilter(setRole)(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutlineRoundedIcon fontSize="small" color="secondary" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiInputBase-root": { height: 44, borderRadius: 1.5 },
                        "& .MuiSelect-icon": { display: "inline-block" },
                      }}
                      SelectProps={{
                        MenuProps: { PaperProps: { style: { maxHeight: 300 } } },
                      }}
                    >
                      {ROLE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      select
                      label="Verified Dealer"
                      fullWidth
                      size="medium"
                      value={isVerifiedDealer}
                      onChange={(e) => updateFilter(setIsVerifiedDealer)(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <VerifiedRoundedIcon fontSize="small" color="secondary" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiInputBase-root": { height: 44, borderRadius: 1.5 },
                        "& .MuiSelect-icon": { display: "inline-block" },
                      }}
                      SelectProps={{
                        MenuProps: { PaperProps: { style: { maxHeight: 300 } } },
                      }}
                    >
                      {BOOL_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      select
                      label="Verified"
                      fullWidth
                      size="medium"
                      value={verifiedBadge}
                      onChange={(e) => updateFilter(setVerifiedBadge)(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <VerifiedRoundedIcon fontSize="small" color="secondary" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiInputBase-root": { height: 44, borderRadius: 1.5 },
                        "& .MuiSelect-icon": { display: "inline-block" },
                      }}
                      SelectProps={{
                        MenuProps: { PaperProps: { style: { maxHeight: 300 } } },
                      }}
                    >
                      {BOOL_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      select
                      label="Active"
                      fullWidth
                      size="medium"
                      value={isActive}
                      onChange={(e) => updateFilter(setIsActive)(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ToggleOnRoundedIcon fontSize="small" color="secondary" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiInputBase-root": { height: 44, borderRadius: 1.5 },
                        "& .MuiSelect-icon": { display: "inline-block" },
                      }}
                      SelectProps={{
                        MenuProps: { PaperProps: { style: { maxHeight: 300 } } },
                      }}
                    >
                      {BOOL_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </MDBox>

              <Divider />

              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={{ defaultValue: 10, entries: [10, 15, 20, 35, 50] }}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  page={page}
                  onPageChange={setPage}
                  pageEntryCount={updateFilter(setPageCount)}
                  manualPagination
                  pageCount={totalPages}
                  totalEntries={totalUsers}
                  searchValue={search}
                  onSearchChange={setSearch}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default UserManagement;
